import type { TreeEntry } from 'codesearch-backend'
import { createEffect, createSignal, For, Show } from 'solid-js'
import { openBrowse, openFile, useTree } from '../store/view.ts'
import { cn } from '../utils/cn.ts'

function TreeNode(props: { repo: string, entry: TreeEntry, depth: number, activePath?: string }) {
  const [expanded, setExpanded] = createSignal(false)
  const children = useTree(() => (expanded() ? props.repo : undefined), () => props.entry.path)
  const isActive = () => props.activePath === props.entry.path

  // opening a file should reveal it in the tree, which means every directory on the
  // way down has to expand itself - including later, when the active file changes
  // while the tree is already on screen
  createEffect(() => {
    if (props.activePath?.startsWith(`${props.entry.path}/`)) setExpanded(true)
  })

  return (
    <>
      <div
        class={cn(
          'flex cursor-pointer items-center gap-1 py-0.25 pr-2 text-xs transition-colors hover:bg-muted/50',
          isActive() && 'bg-accent/40',
        )}
        style={{ 'padding-left': `${props.depth * 12 + 6}px` }}
        onClick={() => {
          if (props.entry.isDir) setExpanded(it => !it)
          else openFile(props.repo, props.entry.path)
        }}
      >
        <Show when={props.entry.isDir} fallback={<div class="size-3 shrink-0" />}>
          <div
            class={cn(
              'size-3 shrink-0 text-muted-foreground',
              expanded() ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right',
            )}
          />
        </Show>
        <div
          class={cn(
            'size-3 shrink-0 text-muted-foreground',
            props.entry.isDir ? 'i-lucide-folder' : 'i-lucide-file',
          )}
        />
        <span class="truncate">{props.entry.name}</span>
      </div>

      <Show when={expanded()}>
        <For each={children.data ?? []}>
          {child => (
            <TreeNode repo={props.repo} entry={child} depth={props.depth + 1} activePath={props.activePath} />
          )}
        </For>
      </Show>
    </>
  )
}

export function FileTree(props: { repo: string, activePath?: string }) {
  const root = useTree(() => props.repo, () => '')

  return (
    <div class="h-full flex flex-col overflow-hidden">
      <div
        class="shrink-0 cursor-pointer truncate border-b border-border bg-muted/40 px-2 py-1 text-xs text-muted-foreground font-medium hover:text-foreground"
        onClick={() => openBrowse(props.repo)}
      >
        {props.repo}
      </div>
      <div class="min-h-0 flex-1 overflow-auto py-1">
        <Show when={root.data} fallback={<div class="px-2 py-1 text-xs text-muted-foreground">loading...</div>}>
          <For each={root.data!}>
            {entry => <TreeNode repo={props.repo} entry={entry} depth={0} activePath={props.activePath} />}
          </For>
        </Show>
      </div>
    </div>
  )
}
